'use client';

import { useEffect, useRef } from 'react';

export interface TurnstileWidgetProps {
    siteKey: string;
    action: string;
    onSuccess: (token: string) => void;
    onError?: (error: string) => void;
    onExpire?: () => void;
    enabled?: boolean;
    mode?: 'managed' | 'non-interactive' | 'invisible';
    /** Mutable ref that receives a reset function. Call it to invalidate the current token and start a new challenge. */
    resetRef?: React.MutableRefObject<(() => void) | null>;
    /** Mutable ref that receives an execute function. Call it to explicitly start the challenge (only used when execution='execute'). */
    executeRef?: React.MutableRefObject<(() => void) | null>;
}

export function TurnstileWidget({
    siteKey,
    action,
    onSuccess,
    onError,
    onExpire,
    enabled = true,
    mode = 'managed',
    resetRef,
    executeRef,
}: TurnstileWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const hasLoggedErrorRef = useRef<boolean>(false);

    /**
     * Callbacks are kept in refs so the render effect below never depends on their identity.
     * Consumers pass inline arrows, and a token that updates parent state would otherwise
     * re-run the effect, destroy the widget and start a new challenge — an endless loop.
     */
    const callbacksRef = useRef({ onSuccess, onError, onExpire });
    useEffect(() => {
        callbacksRef.current = { onSuccess, onError, onExpire };
    }, [onSuccess, onError, onExpire]);

    // Expose a reset function so the parent can request a fresh token
    useEffect(() => {
        if (resetRef) {
            resetRef.current = () => {
                if (widgetIdRef.current && window.turnstile) {
                    window.turnstile.reset(widgetIdRef.current);
                    // A reset returns the widget to its pre-execution state. With
                    // no UI to trigger it, an invisible challenge would never run
                    // again — so a retry after a wrong code would stay blocked.
                    if (mode === 'invisible' || (mode === 'non-interactive' && !executeRef)) {
                        window.turnstile.execute(widgetIdRef.current);
                    }
                }
            };
        }
        return () => {
            if (resetRef) {
                resetRef.current = null;
            }
        };
    }, [resetRef, mode, executeRef]);

    useEffect(() => {
        if (executeRef) {
            executeRef.current = () => {
                if (widgetIdRef.current && window.turnstile) {
                    window.turnstile.execute(widgetIdRef.current);
                }
            };
        }
        return () => {
            if (executeRef) {
                executeRef.current = null;
            }
        };
    }, [executeRef]);

    useEffect(() => {
        if (!enabled || !siteKey || !containerRef.current) {
            return;
        }

        const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        const TURNSTILE_SCRIPT_ID = 'turnstile-script';

        let isCancelled = false;
        let checkInterval: ReturnType<typeof setInterval> | undefined;
        let loadTimeout: ReturnType<typeof setTimeout> | undefined;

        const reportError = (message: string, cause?: unknown) => {
            if (!hasLoggedErrorRef.current) {
                // eslint-disable-next-line no-console
                console.warn(`[Turnstile] ${message}`, cause ?? '');
                hasLoggedErrorRef.current = true;
            }
            callbacksRef.current.onError?.(message);
        };

        // Initialize Turnstile widget
        const initializeWidget = () => {
            if (isCancelled || !window.turnstile || !containerRef.current || widgetIdRef.current) {
                return;
            }

            try {
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    action,
                    callback: (token: string) => {
                        callbacksRef.current.onSuccess(token);
                    },
                    'error-callback': () => {
                        reportError('Challenge failed');
                    },
                    'expired-callback': () => {
                        callbacksRef.current.onExpire?.();
                    },
                    // `interaction-only` already hides the widget unless Cloudflare
                    // asks for interaction. `invisible` goes further: the challenge
                    // never renders UI, so a flow that must not show a second
                    // checkbox (e.g. the OTP drawer) can opt out entirely.
                    appearance: mode === 'invisible' ? 'execute' : 'interaction-only',
                    execution: mode === 'managed' ? 'render' : 'execute',
                    theme: 'light',
                    size: 'normal',
                });

                // Deferred consumers can supply executeRef to choose when to run.
                // Otherwise both non-interactive and invisible challenges must
                // start here, once the asynchronously loaded widget exists.
                if ((mode === 'invisible' || (mode === 'non-interactive' && !executeRef)) && widgetIdRef.current) {
                    window.turnstile.execute(widgetIdRef.current);
                }
            } catch (error) {
                reportError(`Failed to initialize: ${error instanceof Error ? error.message : String(error)}`, error);
            }
        };

        // Check if Turnstile is already loaded
        if (window.turnstile) {
            initializeWidget();
        } else {
            // Load Turnstile script if not already present
            let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

            if (!script) {
                script = document.createElement('script');
                script.id = TURNSTILE_SCRIPT_ID;
                script.src = TURNSTILE_SCRIPT_URL;
                script.async = true;
                script.defer = true;

                script.onload = () => {
                    initializeWidget();
                };

                script.onerror = () => {
                    reportError('Failed to load script');
                };

                document.head.appendChild(script);
            } else {
                // Script exists (another widget added it), wait for it to load
                checkInterval = setInterval(() => {
                    if (window.turnstile) {
                        clearInterval(checkInterval);
                        initializeWidget();
                    }
                }, 100);

                loadTimeout = setTimeout(() => {
                    clearInterval(checkInterval);
                    if (!widgetIdRef.current) {
                        reportError('Script load timeout');
                    }
                }, 10000);
            }
        }

        // Cleanup
        return () => {
            isCancelled = true;
            clearInterval(checkInterval);
            clearTimeout(loadTimeout);
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [action, enabled, siteKey, mode, executeRef]);

    if (!enabled || !siteKey) {
        return null;
    }

    return (
        <div className="space-y-2 flex justify-start">
            <div ref={containerRef} data-testid="turnstile-widget" role="presentation" />
        </div>
    );
}

declare global {
    interface Window {
        turnstile?: {
            render: (
                container: string | HTMLElement,
                options: {
                    sitekey: string;
                    action?: string;
                    callback?: (token: string) => void;
                    'error-callback'?: () => void;
                    'expired-callback'?: () => void;
                    appearance?: 'always' | 'execute' | 'interaction-only';
                    execution?: 'render' | 'execute';
                    theme?: 'light' | 'dark' | 'auto';
                    size?: 'normal' | 'compact';
                }
            ) => string;
            execute: (widgetId: string) => void;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
            getResponse: (widgetId: string) => string;
        };
    }
}
